import { Home, FileText, Shield, Users, Settings } from 'lucide-react';
import { Breadcrumb } from '@components/base/navigation';
import { Icon, Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

function BreadcrumbShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="breadcrumb">
          <Breadcrumb separator={playgroundProps.separator as string}>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href="/policies">Policies</Breadcrumb.Item>
            <Breadcrumb.Item href="/policies/auto">Auto Insurance</Breadcrumb.Item>
            <Breadcrumb.Item isCurrent>POL-2024-001842</Breadcrumb.Item>
          </Breadcrumb>
      </ShowcasePreviewSection>

      {/* Separator Options */}
      <ShowcaseDemoSection
        title="Separator Options"
        description="Different separator styles for various visual preferences"
       
      >
          <VStack gap={4}>
            {([
              { label: 'Slash separator (default)', sep: '/' },
              { label: 'Chevron separator', sep: '>' },
              { label: 'Arrow separator', sep: '\u2192' },
              { label: 'Pipe separator', sep: '|' },
            ] as const).map(({ label, sep }) => (
              <div key={sep}>
                <Text size="sm" color="secondary" className="mb-2">{label}</Text>
                <Breadcrumb separator={sep}>
                  <Breadcrumb.Item href="/">Dashboard</Breadcrumb.Item>
                  <Breadcrumb.Item href="/claims">Claims</Breadcrumb.Item>
                  <Breadcrumb.Item isCurrent>CLM-2024-00567</Breadcrumb.Item>
                </Breadcrumb>
              </div>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* With Icons */}
      <ShowcaseDemoSection
        title="With Icons"
        description="Breadcrumb items can include icons for better navigation context"
       
      >
          <VStack gap={4}>
            <Breadcrumb>
              <Breadcrumb.Item href="/">
                <HStack gap={1} align="center">
                  <Icon icon={Home} size="sm" />
                  <span>Home</span>
                </HStack>
              </Breadcrumb.Item>
              <Breadcrumb.Item href="/policies">
                <HStack gap={1} align="center">
                  <Icon icon={Shield} size="sm" />
                  <span>Policies</span>
                </HStack>
              </Breadcrumb.Item>
              <Breadcrumb.Item isCurrent>
                <HStack gap={1} align="center">
                  <Icon icon={FileText} size="sm" />
                  <span>Policy Details</span>
                </HStack>
              </Breadcrumb.Item>
            </Breadcrumb>

            <Breadcrumb separator=">">
              <Breadcrumb.Item href="/">
                <HStack gap={1} align="center">
                  <Icon icon={Home} size="sm" />
                  <span>Home</span>
                </HStack>
              </Breadcrumb.Item>
              <Breadcrumb.Item href="/customers">
                <HStack gap={1} align="center">
                  <Icon icon={Users} size="sm" />
                  <span>Customers</span>
                </HStack>
              </Breadcrumb.Item>
              <Breadcrumb.Item href="/customers/settings">
                <HStack gap={1} align="center">
                  <Icon icon={Settings} size="sm" />
                  <span>Settings</span>
                </HStack>
              </Breadcrumb.Item>
              <Breadcrumb.Item isCurrent>Preferences</Breadcrumb.Item>
            </Breadcrumb>
          </VStack>
      </ShowcaseDemoSection>

      {/* Truncation (Long Paths) */}
      <ShowcaseDemoSection
        title="Truncation (Long Paths)"
        description="Long breadcrumb trails are automatically truncated with an expandable ellipsis menu"
       
      >
          <VStack gap={4}>
            <div>
              <Text size="sm" color="secondary" className="mb-2">Default maxItems (4) - truncates middle items</Text>
              <Breadcrumb>
                <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                <Breadcrumb.Item href="/insurance">Insurance Products</Breadcrumb.Item>
                <Breadcrumb.Item href="/insurance/commercial">Commercial Lines</Breadcrumb.Item>
                <Breadcrumb.Item href="/insurance/commercial/liability">Liability Coverage</Breadcrumb.Item>
                <Breadcrumb.Item href="/insurance/commercial/liability/general">General Liability</Breadcrumb.Item>
                <Breadcrumb.Item isCurrent>Policy Application</Breadcrumb.Item>
              </Breadcrumb>
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">maxItems=3 - more aggressive truncation</Text>
              <Breadcrumb maxItems={3}>
                <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                <Breadcrumb.Item href="/claims">Claims</Breadcrumb.Item>
                <Breadcrumb.Item href="/claims/property">Property Claims</Breadcrumb.Item>
                <Breadcrumb.Item href="/claims/property/water">Water Damage</Breadcrumb.Item>
                <Breadcrumb.Item isCurrent>CLM-2024-00892</Breadcrumb.Item>
              </Breadcrumb>
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">maxItems=6 - less truncation</Text>
              <Breadcrumb maxItems={6}>
                <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                <Breadcrumb.Item href="/admin">Administration</Breadcrumb.Item>
                <Breadcrumb.Item href="/admin/users">User Management</Breadcrumb.Item>
                <Breadcrumb.Item href="/admin/users/agents">Insurance Agents</Breadcrumb.Item>
                <Breadcrumb.Item href="/admin/users/agents/region-west">West Region</Breadcrumb.Item>
                <Breadcrumb.Item href="/admin/users/agents/region-west/team-a">Team Alpha</Breadcrumb.Item>
                <Breadcrumb.Item isCurrent>John Smith</Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </VStack>
      </ShowcaseDemoSection>

      {/* Basic Usage */}
      <ShowcaseDemoSection
        title="Basic Usage"
        description="Simple breadcrumb navigation for common insurance portal paths"
       
      >
          <VStack gap={4}>
            <div>
              <Text size="sm" color="secondary" className="mb-2">Policy navigation</Text>
              <Breadcrumb>
                <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                <Breadcrumb.Item href="/policies">My Policies</Breadcrumb.Item>
                <Breadcrumb.Item isCurrent>Auto Insurance</Breadcrumb.Item>
              </Breadcrumb>
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">Claims navigation</Text>
              <Breadcrumb>
                <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                <Breadcrumb.Item href="/claims">Claims Center</Breadcrumb.Item>
                <Breadcrumb.Item href="/claims/new">File New Claim</Breadcrumb.Item>
                <Breadcrumb.Item isCurrent>Upload Documents</Breadcrumb.Item>
              </Breadcrumb>
            </div>

            <div>
              <Text size="sm" color="secondary" className="mb-2">Account settings</Text>
              <Breadcrumb>
                <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                <Breadcrumb.Item href="/account">Account</Breadcrumb.Item>
                <Breadcrumb.Item isCurrent>Payment Methods</Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const BreadcrumbShowcase: ShowcaseDefinition = {
  id: 'breadcrumb',
  name: 'Breadcrumb',
  category: 'navigation',
  description: 'Navigation trail showing current page location',
  propsDefinition: [
    {
      name: 'separator',
      type: 'select',
      options: ['/', '>', '→', '|'],
      defaultValue: '/',
      description: 'Character used between breadcrumb items',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'nav element with aria-label="Breadcrumb"',
      'aria-current="page" on current/last item',
      'Separator elements have aria-hidden="true"',
    ],
    keyboardNav: [
      'Tab: Navigate between breadcrumb links',
      'Enter: Follow breadcrumb link',
      'Escape: Close ellipsis dropdown (when open)',
    ],
    notes: [
      'Uses semantic nav and ol/li structure',
      'Ellipsis menu is keyboard accessible',
      'Current page is not a link (prevents confusion)',
    ],
  },
  component: BreadcrumbShowcaseComponent,
};
